import type { IOverlapOvertimeContext } from "app/pages/website-analysis/audience-overlap/OverlapOverTime/context";
import type { IData } from "app/pages/website-analysis/audience-overlap/SharedVisitorsTable/sharedTypes";
import type { IChartDataAll, IChartData } from "app/pages/website-analysis/audience-overlap/OverlapOverTime/types";
import { EMetricType } from "app/pages/website-analysis/audience-overlap/OverlapOverTime/types";
import dayjs from "dayjs";
import { colorsPalettes } from "@similarweb/styles";
import type { ISiteLegendItems } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartContainer";
import { sortArrayAsOtherArray } from "app/UtilitiesAndConstants/UtilityFunctions/sort";

const SITES_DELIMITER = ",";
const { TOTAL, PER_SITE } = EMetricType;

export const parseChartData = (
    overlapOvertimeContext: IOverlapOvertimeContext,
    rawData: IData,
    chosenSitesLegends: ISiteLegendItems,
): IChartDataAll => {
    if (!rawData || !rawData.Graph) {
        return { [TOTAL]: [], [PER_SITE]: [] };
    }
    const chartDataTotal = parseTotal(overlapOvertimeContext, rawData);
    const chartDataPerSite = parsePerSite(overlapOvertimeContext, rawData, chosenSitesLegends);
    return { [TOTAL]: chartDataTotal, [PER_SITE]: chartDataPerSite };
};

const getAllSitesSet = (overtimeData) => {
    // allSitesSets === the biggest set
    return Object.keys(overtimeData).sort((setA, setB) =>
        setA.split(SITES_DELIMITER).length > setB.split(SITES_DELIMITER).length ? -1 : 1,
    )[0];
};

const parseTotal = (overlapOvertimeContext: IOverlapOvertimeContext, rawData: IData): IChartData => {
    const overtimeData = rawData.Graph;
    const allSitesSet = getAllSitesSet(overtimeData);

    const chartData = [
        {
            name: "total",
            color: colorsPalettes.blue[400],
            data: overtimeData[allSitesSet].map((dataItem) => {
                const { Key, Value } = dataItem;
                return {
                    x: dayjs.utc(Key).valueOf(),
                    y: Value,
                };
            }),
        },
    ];
    return chartData;
};
const parsePerSite = (
    overlapOvertimeContext: IOverlapOvertimeContext,
    rawData: IData,
    websitesArrayWithColor: ISiteLegendItems,
): IChartData => {
    const overtimeData = rawData.Graph;
    const oneSiteSets = Object.keys(overtimeData).filter((set) => set.split(SITES_DELIMITER).length === 1);
    const allSitesSet = getAllSitesSet(overtimeData);
    const chartData = oneSiteSets.map((set) => {
        const siteMd = websitesArrayWithColor.find(({ name }) => name === set);
        return {
            name: set,
            color: siteMd?.color,
            isSuggested: siteMd?.isSuggested,
            data: overtimeData[set].map((dataItem, index) => {
                const { Key, Value } = dataItem;
                const y = overtimeData[allSitesSet][index].Value / Value;
                return {
                    x: dayjs.utc(Key).valueOf(),
                    y,
                    uniqueUsers: overtimeData[set][index].Value,
                };
            }),
        };
    });
    const sortedChartData = sortArrayAsOtherArray(chartData, websitesArrayWithColor, "name");
    return sortedChartData;
};
